import React from 'react';
import { Box } from '@mui/material';
import { CARD_BRAND_LABELS, type CardBrandCode } from '../../utils/cardBrands';

type SingleBrand = Exclude<CardBrandCode, 'default'> | 'visa' | 'mastercard';

const W = 40;
const H = 26;

const Frame: React.FC<{ bg: string; border?: string; children: React.ReactNode }> = ({ bg, border, children }) => (
  <>
    <rect x="0.5" y="0.5" width={W - 1} height={H - 1} rx="4" fill={bg} stroke={border || 'rgba(15,23,42,0.14)'} />
    {children}
  </>
);

const text = (props: React.SVGProps<SVGTextElement>, value: string) => (
  <text
    textAnchor="middle"
    fontFamily="Arial, Helvetica, sans-serif"
    fontWeight={700}
    {...props}
  >
    {value}
  </text>
);

function renderBrand(brand: SingleBrand) {
  switch (brand) {
    case 'visa':
      return (
        <Frame bg="#ffffff">
          {text({ x: 20, y: 17.5, fontSize: 12, fill: '#1a1f71', fontStyle: 'italic', letterSpacing: 0.5 }, 'VISA')}
        </Frame>
      );
    case 'mastercard':
      return (
        <Frame bg="#ffffff">
          <circle cx="16" cy="13" r="7.5" fill="#eb001b" />
          <circle cx="24" cy="13" r="7.5" fill="#f79e1b" />
          <path d="M20 7.1a7.5 7.5 0 0 1 0 11.8a7.5 7.5 0 0 1 0-11.8z" fill="#ff5f00" />
        </Frame>
      );
    case 'elo':
      return (
        <Frame bg="#000000" border="#000000">
          <circle cx="9" cy="8" r="2.4" fill="#ffcb05" />
          <circle cx="9" cy="13" r="2.4" fill="#00a4e0" />
          <circle cx="9" cy="18" r="2.4" fill="#ef4123" />
          {text({ x: 24, y: 17.5, fontSize: 12, fill: '#ffffff' }, 'elo')}
        </Frame>
      );
    case 'amex':
      return (
        <Frame bg="#2e77bc" border="#2e77bc">
          {text({ x: 20, y: 15.5, fontSize: 8.5, fill: '#ffffff', letterSpacing: 0.4 }, 'AMEX')}
          <rect x="6" y="18" width="28" height="1.2" fill="#ffffff" opacity="0.7" />
        </Frame>
      );
    case 'hipercard':
      return (
        <Frame bg="#b3131b" border="#b3131b">
          {text({ x: 20, y: 16, fontSize: 6.6, fill: '#ffffff', fontStyle: 'italic' }, 'Hipercard')}
        </Frame>
      );
    case 'diners':
      return (
        <Frame bg="#ffffff">
          <circle cx="20" cy="13" r="8" fill="#0079be" />
          <circle cx="20" cy="13" r="5.6" fill="#ffffff" />
          <rect x="19.3" y="7.4" width="1.4" height="11.2" fill="#0079be" />
        </Frame>
      );
    case 'discover':
      return (
        <Frame bg="#ffffff">
          {text({ x: 11.5, y: 14.8, fontSize: 5.4, fill: '#231f20' }, 'DISC')}
          <circle cx="21.6" cy="12.9" r="2.6" fill="#f58220" />
          {text({ x: 30, y: 14.8, fontSize: 5.4, fill: '#231f20' }, 'VER')}
          <path d="M18 25.5 L39.5 19 L39.5 25.5 Z" fill="#f58220" opacity="0.9" />
        </Frame>
      );
    case 'jcb':
      return (
        <Frame bg="#ffffff">
          <rect x="7" y="5" width="8" height="16" rx="2.5" fill="#0e4c96" />
          <rect x="16" y="5" width="8" height="16" rx="2.5" fill="#e21836" />
          <rect x="25" y="5" width="8" height="16" rx="2.5" fill="#00a650" />
          {text({ x: 11, y: 15.5, fontSize: 7, fill: '#ffffff' }, 'J')}
          {text({ x: 20, y: 15.5, fontSize: 7, fill: '#ffffff' }, 'C')}
          {text({ x: 29, y: 15.5, fontSize: 7, fill: '#ffffff' }, 'B')}
        </Frame>
      );
    case 'cabal':
      return (
        <Frame bg="#0b3f8f" border="#0b3f8f">
          {text({ x: 20, y: 16.5, fontSize: 9, fill: '#ffffff', letterSpacing: 0.3 }, 'cabal')}
        </Frame>
      );
    case 'banescard':
      return (
        <Frame bg="#ffffff">
          <rect x="3.5" y="9.5" width="3.5" height="7" rx="1" fill="#00843d" />
          {text({ x: 23, y: 15.6, fontSize: 5.4, fill: '#004a8f' }, 'banescard')}
        </Frame>
      );
    default:
      return null;
  }
}

const SingleLogo: React.FC<{ brand: SingleBrand; height: number }> = ({ brand, height }) => (
  <Box
    component="svg"
    viewBox={`0 0 ${W} ${H}`}
    sx={{ height, width: (height * W) / H, display: 'block', flexShrink: 0 }}
    aria-hidden
  >
    {renderBrand(brand)}
  </Box>
);

/** Símbolo da bandeira; 'default' mostra Mastercard + Visa lado a lado. */
const CardBrandLogo: React.FC<{ brand: CardBrandCode; height?: number; title?: string }> = ({
  brand,
  height = 22,
  title,
}) => (
  <Box
    component="span"
    title={title ?? CARD_BRAND_LABELS[brand]}
    role="img"
    aria-label={CARD_BRAND_LABELS[brand]}
    sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
  >
    {brand === 'default' ? (
      <>
        <SingleLogo brand="mastercard" height={height} />
        <SingleLogo brand="visa" height={height} />
      </>
    ) : (
      <SingleLogo brand={brand} height={height} />
    )}
  </Box>
);

export default CardBrandLogo;
